import {
  Addon,
  AssetTag,
  cacheKeys,
  isAssetReadOnly,
  PermissionValues,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useDeleteMutation,
  useProduct,
  useUpdateAsset,
  useWorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  AssetTagGallery,
  Button,
  Colors,
  ConfirmationOverlay,
  ControlledCheckboxItems,
  ControlledTextInput,
  GroupSelector,
  LoadingIndicator,
  PinnedActivities,
  SlideAction,
  SlideLayout,
  type ColumnData,
  type SlideLayoutLocationState,
  Spacing,
  Toggle,
  tokens,
  usePinnedActivities,
  useSlideLayoutSafe,
  ViewMode,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { array, boolean, string, object as yupObject } from 'yup';
import { useUnsavedChangesBlocker } from '../../shared/hooks/useUnsavedChangesBlocker';
import { ExportButton } from '../../accounts/components/dataExport/exportButton';
import { useActivityExport } from '../../accounts/components/dataExport/useActivityExport';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';
import { AssetTagQrPanel } from './assetTagQrPanel';
import EditGroup from './editGroup';
import { useAssetTags } from './useAssetTags';
import { WebTagPicker } from './webTagPicker';

const EDIT_ASSET_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/edit.tsx';
const ASSET_SUB_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/assetSubRoutes.tsx';
const ASSET_CARD_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const EDIT_GROUP_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/editGroup.tsx';
const ASSET_TAG_QR_PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetTagQrPanel.tsx';
const ASSET_EDIT_ROUTE_PANEL_KEY = 'asset-edit';
const ASSET_EDIT_ROUTE_PATTERN = '/assets/:assetId/edit';
const ACTIVITY_ASSET_EDIT_ROUTE_PATTERN =
  '/activities/:issueId/asset/:assetId/edit';
const ASSET_CARD_PANEL_DEBUG_ID = 'asset-card';

export const EditAsset = () => {
  return <EditAssetBase />;
};

export const EditAssetBase = () => {
  const { assetId = '', issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedGroups, setSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isGroupsPanelOpen, setIsGroupsPanelOpen] = useState(false);
  // Tag whose printed Gripp Tag (QR) is shown as the third column, if any.
  // Mutually exclusive with the groups editor — opening one closes the other.
  const [qrTag, setQrTag] = useState<AssetTag | null>(null);
  const locationState = location.state as SlideLayoutLocationState | null;
  const routePattern = issueId
    ? ACTIVITY_ASSET_EDIT_ROUTE_PATTERN
    : ASSET_EDIT_ROUTE_PATTERN;
  const assetRouteValue = {
    basePath: issueId
      ? `/activities/${issueId}/asset/${assetId}`
      : `/assets/${assetId}`,
    listPath: issueId ? '/activities' : '/assets',
    isWorkspaceMode: false,
    issueId,
  };
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const { workspaceAsset: leftColumnWorkspaceAsset } = useWorkspaceAsset(
    assetId,
    !assetId
  );
  const leftColumnRawTitle = leftColumnWorkspaceAsset?.asset?.name || undefined;

  if (!assetId) {
    return null;
  }

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    rawTitle: leftColumnRawTitle,
    mainComponent: (
      <AssetRouteProvider value={assetRouteValue}>
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(issueId ? `/activities/${issueId}` : '/assets', {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    debug: {
      id: ASSET_CARD_PANEL_DEBUG_ID,
      titleKey: 'assets.menu.title',
      rawTitle: leftColumnRawTitle,
      component: 'AssetCard',
      componentSource: ASSET_CARD_SOURCE,
      columnSource: EDIT_ASSET_SOURCE,
      ownerComponent: 'EditAssetBase',
      ownerSource: EDIT_ASSET_SOURCE,
      routePanelKey: ASSET_EDIT_ROUTE_PANEL_KEY,
      routePattern,
      routeSource: ASSET_SUB_ROUTES_SOURCE,
      source: {
        owner: {
          file: EDIT_ASSET_SOURCE,
          line: 82,
          symbol: 'EditAssetBase',
        },
        column: {
          file: EDIT_ASSET_SOURCE,
          line: 104,
          symbol: 'leftColumn',
        },
        component: {
          file: ASSET_CARD_SOURCE,
          line: 40,
          symbol: 'AssetCard',
        },
        route: {
          file: ASSET_SUB_ROUTES_SOURCE,
          line: 37,
          symbol: 'assetSubRoutes',
        },
      },
      entity: { type: 'asset', id: assetId },
    },
  };

  const rightColumn: ColumnData = {
    title: 'asset.editTitle',
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () => {
      navigate('..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
    mainComponent: (
      <AssetRouteProvider value={assetRouteValue}>
        <EditAssetComponent
          assetId={assetId}
          selectedGroups={selectedGroups}
          onGroupsChange={setSelectedGroups}
          onOpenGroupEditor={() => {
            setQrTag(null);
            setIsGroupsPanelOpen(true);
          }}
          onViewTagQr={(tag) => {
            setIsGroupsPanelOpen(false);
            setQrTag(tag);
          }}
        />
      </AssetRouteProvider>
    ),
    debug: {
      id: 'asset-edit:right-edit-asset',
      titleKey: 'asset.editTitle',
      component: 'EditAssetComponent',
      componentSource: EDIT_ASSET_SOURCE,
      columnSource: EDIT_ASSET_SOURCE,
      ownerComponent: 'EditAssetBase',
      ownerSource: EDIT_ASSET_SOURCE,
      routePanelKey: ASSET_EDIT_ROUTE_PANEL_KEY,
      routePattern,
      routeSource: ASSET_SUB_ROUTES_SOURCE,
      source: {
        owner: {
          file: EDIT_ASSET_SOURCE,
          line: 82,
          symbol: 'EditAssetBase',
        },
        column: {
          file: EDIT_ASSET_SOURCE,
          line: 166,
          symbol: 'rightColumn',
        },
        component: {
          file: EDIT_ASSET_SOURCE,
          line: 284,
          symbol: 'EditAssetComponent',
        },
        route: {
          file: ASSET_SUB_ROUTES_SOURCE,
          line: 37,
          symbol: 'assetSubRoutes',
        },
      },
      entity: { type: 'assetEdit', id: assetId },
    },
  };

  const thirdColumn = isGroupsPanelOpen
    ? {
        title: 'asset.groups',
        mainComponent: (
          <EditGroup
            assetId={assetId}
            selectedGroups={selectedGroups}
            onGroupsChange={setSelectedGroups}
          />
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsGroupsPanelOpen(false),
        debug: {
          id: 'asset-edit:third-edit-groups',
          titleKey: 'asset.groups',
          component: 'EditGroup',
          componentSource: EDIT_GROUP_SOURCE,
          columnSource: EDIT_ASSET_SOURCE,
          ownerComponent: 'EditAssetBase',
          ownerSource: EDIT_ASSET_SOURCE,
          routePanelKey: ASSET_EDIT_ROUTE_PANEL_KEY,
          routePattern,
          routeSource: ASSET_SUB_ROUTES_SOURCE,
          source: {
            owner: {
              file: EDIT_ASSET_SOURCE,
              line: 82,
              symbol: 'EditAssetBase',
            },
            column: {
              file: EDIT_ASSET_SOURCE,
              line: 222,
              symbol: 'thirdColumn',
            },
            component: {
              file: EDIT_GROUP_SOURCE,
              line: 20,
              symbol: 'EditGroup',
            },
            route: {
              file: ASSET_SUB_ROUTES_SOURCE,
              line: 37,
              symbol: 'assetSubRoutes',
            },
          },
          entity: { type: 'assetGroups', id: assetId },
        },
      }
    : qrTag
    ? {
        title: 'asset.qr.title',
        rawTitle: qrTag.tagCode,
        mainComponent: <AssetTagQrPanel tagCode={qrTag.tagCode} />,
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setQrTag(null),
        debug: {
          id: 'asset-edit:third-tag-qr',
          titleKey: 'asset.qr.title',
          rawTitle: qrTag.tagCode,
          component: 'AssetTagQrPanel',
          componentSource: ASSET_TAG_QR_PANEL_SOURCE,
          columnSource: EDIT_ASSET_SOURCE,
          ownerComponent: 'EditAssetBase',
          ownerSource: EDIT_ASSET_SOURCE,
          routePanelKey: ASSET_EDIT_ROUTE_PANEL_KEY,
          routePattern,
          routeSource: ASSET_SUB_ROUTES_SOURCE,
          source: {
            owner: {
              file: EDIT_ASSET_SOURCE,
              line: 86,
              symbol: 'EditAssetBase',
            },
            column: {
              file: EDIT_ASSET_SOURCE,
              line: 296,
              symbol: 'thirdColumn',
            },
            component: {
              file: ASSET_TAG_QR_PANEL_SOURCE,
              line: 16,
              symbol: 'AssetTagQrPanel',
            },
            route: {
              file: ASSET_SUB_ROUTES_SOURCE,
              line: 40,
              symbol: 'assetSubRoutes',
            },
          },
          entity: { type: 'assetTagQr', id: assetId },
        },
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

export const EditAssetComponent = ({
  assetId,
  selectedGroups,
  onGroupsChange,
  onOpenGroupEditor,
  onViewTagQr,
}: {
  assetId: string;
  selectedGroups: Array<{ id: string; name: string }>;
  onGroupsChange: (groups: Array<{ id: string; name: string }>) => void;
  /**
   * Invoked when the user clicks the "+" button in the Groups section.
   * Parents that host this component in a SlideLayout should set this to
   * open the group editor (e.g. as a fourth column). When omitted, the "+"
   * button is a no-op, which matches contexts where the group editor is
   * already visible alongside the form (e.g. EditAssetBase's own 3-column
   * layout).
   */
  onOpenGroupEditor?: () => void;
  /**
   * Invoked when the user presses the QR icon next to a tag in the gallery.
   * Parents should open the printed-tag QR panel (e.g. as a third column).
   * When omitted, the QR icon on each tag is not pressable.
   */
  onViewTagQr?: (tag: AssetTag) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { basePath, isWorkspaceMode } = useAssetRouting(assetId);
  const workspace = useCurrentWorkspace(true);
  const isOEM = workspace?.type === WorkspaceType.Oem;
  const { updateAsset } = useUpdateAsset({ assetId: assetId! });
  const {
    workspaceAsset,
    isLoading: isLoadingWorkspaceAsset,
    invalidateQuery,
  } = useWorkspaceAsset(assetId!);
  const lastAppliedFormValuesKey = useRef<string | undefined>(undefined);
  const tagIdsRef = useRef<string[] | undefined>(undefined);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const canEdit = !isAssetReadOnly(account, workspaceAsset);
  const canEditName = canEdit;

  // Get workspace from workspaceAsset (more reliable for global admins)
  const workspaceId = workspaceAsset?.workspace?.id;
  const { groups } = useAllWorkspaceGroups(workspaceId);
  const { createGroup } = useCreateGroup();
  const { product, isLoading: isLoadingProduct } = useProduct(
    asset?.product?.id || ''
  );
  const { assetTags, isLoaded: tagsLoaded } = useAssetTags(assetId!);
  const { pinnedActivities } = usePinnedActivities(assetId!);
  const [showAllPinnedActivities, setShowAllPinnedActivities] = useState(false);
  const slideLayout = useSlideLayoutSafe();
  const { mutateAsync: deleteAsset, isPending: isDeletePending } =
    useDeleteMutation({
      modelName: 'asset',
      query: DELETE_ASSET,
    });

  // Sync shared groups state when server data loads or changes
  useEffect(() => {
    if (workspaceAsset?.groups) {
      onGroupsChange(
        workspaceAsset.groups.map((group) => ({
          id: group.id,
          name: group.name,
        }))
      );
    }
  }, [workspaceAsset?.groups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep form in sync with selectedGroups (driven by either GroupSelector or EditGroup).
  // Mark dirty only when the current selection differs from the last server-saved groups.
  useEffect(() => {
    const serverIds = (workspaceAsset?.groups ?? [])
      .map((g) => g.id)
      .sort()
      .join(',');
    const currentIds = selectedGroups
      .map((g) => g.id)
      .sort()
      .join(',');
    setValue(
      'groupIds',
      selectedGroups.map((g) => g.id),
      {
        shouldDirty: serverIds !== currentIds,
        shouldValidate: true,
      }
    );
  }, [selectedGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    tagIds: array().of(string()).optional().nullable(),
    groupIds: array().of(string()).optional().nullable(),
    addons: array().of(string()).optional().nullable(),
    product: string().optional().nullable(),
    serialNumber: string().optional().nullable(),
    hideReportAndNoteOptionsAnonymous: boolean().optional(),
  });

  const formValues = useMemo(
    () => ({
      name: asset?.name ?? '',
      description: asset?.description ?? '',
      details: asset?.details ?? '',
      tagIds: tagsLoaded ? assetTags.map((tag) => tag.id) : undefined,
      groupIds: workspaceAsset?.groups?.map((group) => group.id),
      addons: asset?.addons?.map((addon) => addon.id),
      product: asset?.product?.name ?? '',
      serialNumber: asset?.serialNumber ?? '',
      hideReportAndNoteOptionsAnonymous:
        workspaceAsset?.anonymousOptions?.hideReportAndNote ?? false,
    }),
    [
      asset?.addons,
      asset?.description,
      asset?.details,
      asset?.name,
      asset?.product?.name,
      asset?.serialNumber,
      assetTags,
      tagsLoaded,
      workspaceAsset?.anonymousOptions?.hideReportAndNote,
      workspaceAsset?.groups,
    ]
  );
  const formValuesKey = useMemo(() => JSON.stringify(formValues), [formValues]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    defaultValues: formValues,
  });

  useEffect(() => {
    if (
      !asset ||
      isDirty ||
      lastAppliedFormValuesKey.current === formValuesKey
    ) {
      return;
    }

    reset(formValues);
    tagIdsRef.current = formValues.tagIds;
    lastAppliedFormValuesKey.current = formValuesKey;
  }, [asset, formValues, formValuesKey, isDirty, reset]);

  const addAssetTag = (tag: any) => {
    const tagIds = tagIdsRef.current ?? watch('tagIds') ?? [];
    const nextTagIds = tagIds.includes(tag.id) ? tagIds : [...tagIds, tag.id];

    tagIdsRef.current = nextTagIds;
    setValue('tagIds', nextTagIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const deleteAssetTag = (tagId: string) => {
    const tagIds = tagIdsRef.current ?? watch('tagIds') ?? [];
    const nextTagIds = tagIds.filter(
      (id): id is string => id !== undefined && id !== tagId
    );

    tagIdsRef.current = nextTagIds;
    setValue('tagIds', nextTagIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = useCallback(
    async (data: any) => {
      const { product, ...submissionData } = data;
      const input: any = {
        ...submissionData,
        tagIds: tagIdsRef.current ?? submissionData.tagIds,
        groupIds: selectedGroups.map((group) => group.id),
        id: assetId,
      };
      const savedFormValues = {
        ...data,
        tagIds: input.tagIds,
        groupIds: input.groupIds,
      };

      await (updateAsset as any)(
        { input: input },
        {
          onSuccess: async () => {
            reset(savedFormValues);
            lastAppliedFormValuesKey.current = formValuesKey;
            await queryClient.invalidateQueries({
              queryKey: cacheKeys('workspaceAsset').all,
            });
            await queryClient.invalidateQueries({
              queryKey: cacheKeys('tag').all,
            });
            if (isWorkspaceMode) {
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('workspace').all,
              });
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('asset').all,
              });
            }
          },
        }
      );
    },
    [
      updateAsset,
      reset,
      queryClient,
      assetId,
      selectedGroups,
      isWorkspaceMode,
      formValuesKey,
    ]
  );

  const handleSave = useCallback(
    () => handleSubmit(onSubmit)(),
    [handleSubmit, onSubmit]
  );

  useUnsavedChangesBlocker({ isDirty, isValid, onSave: handleSave });

  const hideDeleteOverlay = () => slideLayout?.setColumnOverlay(null);

  const handleDeleteConfirm = async () => {
    if (isDeletePending || !workspaceAsset) return;
    await deleteAsset(
      {
        input: {
          id: workspaceAsset.asset.id,
          workspaces: [{ id: workspaceAsset.workspace.id }],
        } as any,
      },
      {
        onSuccess: async () => {
          hideDeleteOverlay();
          navigate('/assets');
        },
      }
    );
  };

  const handleDeletePress = () => {
    slideLayout?.setColumnOverlay(
      <ConfirmationOverlay
        title={t('common.confirmDelete', { name: asset?.name })}
        subtitle={t('common.deleteWarning')}
        confirmLabel={t('common.delete')}
        onConfirm={handleDeleteConfirm}
        cancelLabel={t('asset.delete.cancel')}
        onCancel={hideDeleteOverlay}
      />
    );
  };

  if (isLoadingWorkspaceAsset || isLoadingProduct) {
    return <LoadingIndicator />;
  }

  const renderColumnContainer = () => (
    <View style={styles.columnContainer}>
      <View style={[styles.card, styles.cardField]}>
        {isOEM && (
          <View style={styles.formItem}>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.productType')}
            </Text>
            <ControlledTextInput
              control={control}
              name="product"
              editable={false}
              hideBorder
            />
          </View>
        )}
        <View style={styles.formItem}>
          <Text
            style={[
              tokens.typography.label.largeEmphasised,
              styles.formItemTitle,
            ]}
          >
            {t('asset.name')}
          </Text>
          <ControlledTextInput
            control={control}
            name="name"
            placeholder={t('asset.namePlaceholder')}
            editable={canEditName}
            hideBorder
          />
        </View>
        {!isOEM && (
          <View style={styles.formItem}>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.description')}
            </Text>
            <ControlledTextInput
              control={control}
              name="description"
              placeholder={t('asset.descriptionPlaceholder')}
              editable={canEdit}
              hideBorder
            />
          </View>
        )}
        {!isOEM && (
          <View style={styles.formItem}>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.details')}
            </Text>
            <ControlledTextInput
              control={control}
              placeholder={t('asset.detailsPlaceHolder')}
              name="details"
              editable={canEdit}
              textArea
              autoExpand
              hideBorder
            />
          </View>
        )}
        {isOEM && (
          <View style={styles.formItem}>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.serialNumber')}
            </Text>
            <ControlledTextInput
              control={control}
              name="serialNumber"
              editable={canEdit}
              hideBorder
            />
          </View>
        )}
      </View>

      {/* Pinned Activities Section */}
      <PinnedActivities
        assetId={assetId!}
        pinnedActivities={pinnedActivities}
        showAll={showAllPinnedActivities}
        onViewAllPress={() => setShowAllPinnedActivities(true)}
        onActivityPress={(activityId) => {
          // Navigate to activity page
          navigate(`${basePath}/activity/${activityId}`);
        }}
      />

      <View style={styles.card}>
        <View style={styles.formItem}>
          <View>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.groups')}
            </Text>
          </View>
          <GroupSelector
            selectedGroups={selectedGroups}
            availableGroups={groups}
            onGroupsChange={(groups) => {
              onGroupsChange(groups);
            }}
            onCreateGroup={createGroup}
            disabled={!canEdit}
            onSelectorEdit={onOpenGroupEditor}
          />
        </View>
      </View>
      {product && product.addons && product.addons.length > 0 && (
        <View style={styles.card}>
          <View style={styles.formItem}>
            <View>
              <Text
                style={[
                  tokens.typography.label.largeEmphasised,
                  styles.formItemTitle,
                ]}
              >
                {t('asset.addons', { count: asset?.addons?.length || 0 })}
              </Text>
            </View>
            <View style={styles.addonsContainer}>
              <ControlledCheckboxItems
                control={control as any}
                name="addons"
                options={product.addons
                  .slice()
                  .sort((a: Addon, b: Addon) => a.name.localeCompare(b.name))
                  .map((addon: Addon) => ({
                    value: addon.id,
                    label: addon.name,
                  }))}
                style={styles.checkboxItems}
                labelStyle={styles.checkboxItemsLabel}
                position="leading"
                disabled={!canEdit}
              />
            </View>
          </View>
        </View>
      )}
      <View style={[styles.card, styles.cardGroup]}>
        <View style={styles.formItem}>
          <View>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.tagCode')}
            </Text>
          </View>
          <AssetTagGallery
            existingAssetTags={assetTags}
            onAssetTagAdded={addAssetTag}
            onAssetTagDeleted={deleteAssetTag}
            tagPicker={WebTagPicker}
            editable={canEdit}
            onViewTagQr={onViewTagQr}
          />
        </View>
      </View>

      {!isWorkspaceMode && (
        <View style={[styles.card, styles.cardBottom]}>
          <View>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.hideReportAndNote')}
            </Text>
          </View>
          <Controller
            control={control}
            name="hideReportAndNoteOptionsAnonymous"
            render={({ field: { value, onChange } }) => (
              <Toggle
                value={value}
                onValueChange={onChange}
                label={t('asset.hideForAnonymousVisitors')}
                disabled={!canEdit}
                labelStyle={tokens.typography.body.small}
                size="small"
              />
            )}
          />
        </View>
      )}

      {!isWorkspaceMode && asset?.id && workspace && (
        <View style={[styles.card, styles.cardBottom]}>
          <View>
            <Text
              style={[
                tokens.typography.label.largeEmphasised,
                styles.formItemTitle,
              ]}
            >
              {t('asset.activityExport')}
            </Text>
          </View>
          <ExportButton
            useExport={useActivityExport}
            workspace={workspace}
            assetId={asset.id}
            showAssetInfoToggle
            fileName={`${asset.name ?? 'asset'}-activity`}
            label={t('asset.header.activity')}
          />
        </View>
      )}
    </View>
  );

  const canSave = isDirty && isValid;

  if (!asset) {
    return <LoadingIndicator />;
  }

  const canDelete =
    !isWorkspaceMode &&
    workspaceAsset &&
    account?.hasPermission(PermissionValues.asset.delete) &&
    workspace?.type !== WorkspaceType.Oem;

  return (
    <ScrollView
      contentContainerStyle={styles.scrollView}
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
    >
      <View style={styles.container}>{renderColumnContainer()}</View>
      <View style={styles.footer}>
        <Button
          style={styles.saveButton}
          onPress={canSave ? handleSave : undefined}
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
          size="large"
        >
          {t('asset.save')}
        </Button>
        {canDelete && (
          <Button
            variant="rectangular"
            state="outline"
            style={styles.deleteButton}
            onPress={handleDeletePress}
            size="default"
          >
            {t('asset.delete.button')}
          </Button>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  addonsContainer: {
    width: 'auto',
    flexDirection: 'column',
    gap: 0,
    margin: 0,
    padding: 0,
  },
  marginTop: {
    marginTop: 150,
  },
  scrollView: {
    flexGrow: 1,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  container: {
    flexDirection: 'row',
    gap: 14,
  },
  column: {
    maxWidth: 800,
    marginHorizontal: 'auto',
    width: '100%',
  },
  columnContainer: {
    width: '100%',
    gap: 14,
  },
  cardField: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 0,
  },
  card: {
    backgroundColor: Colors.brand.white,
    paddingHorizontal: 16,
    paddingVertical: 0,
    borderRadius: 8,
    gap: 14,
  },
  cardBottom: {
    marginBottom: 10,
    gap: 0,
  },
  cardGroup: {
    marginBottom: 0,
    gap: 0,
  },
  cardHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.black,
  },
  formItem: {
    marginBottom: 10,
  },
  formItemTitle: {
    fontWeight: '600',
    marginBottom: 4,
    color: tokens.colors.slate[800],
  },
  footer: {
    paddingHorizontal: Spacing.baseMargin.base,
    paddingTop: Spacing.basePadding.base * 2,
    backgroundColor: Colors.brand.white,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 4,
  },
  footerActions: {
    gap: 16,
    flexDirection: 'row',
    marginLeft: 'auto',
    marginBottom: 12,
  },
  cancelButton: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.orangeDark,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: Colors.brand.white,
    lineHeight: 16,
    textAlign: 'center',
  },
  saveButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
  contentDelete: {
    maxWidth: 800,
    width: '100%',
    marginHorizontal: 'auto',
    marginTop: 14,
  },
  saveButtonDisabled: {
    backgroundColor: Colors.grayDivider,
    color: Colors.white,
  },
  checkboxItems: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  checkboxItem: {
    gap: 8,
    paddingLeft: 0,
  },
  checkboxItemsLabel: {
    fontSize: 16,
    lineHeight: 19,
    color: Colors.black,
    fontWeight: '400',
    textAlign: 'left',
  },
  thirdPanelToggle: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grayDivider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thirdPanelToggleText: {
    fontSize: 14,
    color: Colors.black,
    letterSpacing: 2,
  },
  thirdPanelBody: {
    fontSize: 14,
    color: Colors.black,
    lineHeight: 20,
  },
  deleteButton: {
    borderWidth: 0,
    width: '100%',
    height: 48,
  },
});

const DELETE_ASSET = `
  mutation Mutation($input: MutateAssetInput!) {
    deleteAsset(input: $input) {
      id
    }
  }
`;
